import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { OpsServer } from '../../../api/dbOps';

/** Bits shared by the phase 3 tabs (Live, Size, Slow, Data fixes, Company data, Alerts). */

export const sel = 'rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-[13px] text-slate-800 focus:outline-2 focus:outline-blue-600';
export const input = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 focus:outline-2 focus:outline-blue-600';
export const th = 'px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400';
export const td = 'px-3 py-2 align-top text-[13px] text-slate-700';

/** "All servers" plus each server. */
export const ServerPicker: React.FC<{ servers: OpsServer[]; value: string; onChange: (v: string) => void; allowAll?: boolean }> = ({
  servers, value, onChange, allowAll = true,
}) => (
  <select className={sel} value={value} onChange={(e) => onChange(e.target.value)} aria-label="Database server">
    {allowAll && <option value="">All servers</option>}
    {servers.map((s) => (
      <option key={s.key} value={s.key}>
        {s.kind === 'registry' ? 'Registry server' : `Tenant server ${s.host}`}
        {s.kind === 'tenant' ? ` (${s.companies.length} ${s.companies.length === 1 ? 'company' : 'companies'})` : ''}
      </option>
    ))}
  </select>
);

/** Seconds as "48 s" / "2 min 10 s" / "3 h 5 min". */
export function secs(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n < 60) return `${Math.round(n * 10) / 10} s`;
  if (n < 3600) return `${Math.floor(n / 60)} min ${Math.round(n % 60)} s`;
  return `${Math.floor(n / 3600)} h ${Math.round((n % 3600) / 60)} min`;
}

export function num(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} M`;
  if (n >= 10_000) return `${Math.round(n / 1000)} K`;
  return n.toLocaleString();
}

/**
 * Load `fn` now and every `everyMs` while the tab is visible (no polling in a
 * hidden tab). `deps` reloads immediately.
 */
export function usePoll<T>(fn: () => Promise<T>, everyMs: number | null, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const load = useCallback(async () => {
    try {
      const d = await fnRef.current();
      setData(d);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    setLoading(true);
    void load();
    if (!everyMs) return undefined;
    const t = window.setInterval(() => { if (document.visibilityState === 'visible') void load(); }, everyMs);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, everyMs, ...deps]);
  return { data, error, loading, reload: load };
}

export const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-[13.5px] text-slate-500">{children}</div>
);
